/**
 * Z.AI SDK Simulation - Interactive API Explorer
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 * 
 * Ultimate Lifetime Tier Feature
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Play, Code, BarChart3, Settings, CheckCircle } from 'lucide-react';

const ZaiSDKSimulation = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('basics');
  const [temperature, setTemperature] = useState(0.7);
  const [topP, setTopP] = useState(0.8);
  const [model, setModel] = useState('glm-4.5');
  const [systemMessage, setSystemMessage] = useState('You are a helpful AI assistant.');
  const [userMessage, setUserMessage] = useState('Hello, please introduce yourself.');
  const [response, setResponse] = useState('');
  const [generating, setGenerating] = useState(false);
  const [metadata, setMetadata] = useState({});

  // Check access - Ultimate tier only
  const hasAccess = user && ['ULTIMATE_ANNUAL', 'ULTIMATE_LIFETIME', 'LIFETIME_ENTERPRISE'].includes(user.subscription_tier);

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-6">
        <div className="max-w-md bg-gray-800 p-8 rounded-xl border border-yellow-500/30 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ultimate Tier Required</h2>
          <p className="text-gray-300 mb-6">
            Z.AI SDK Simulation is exclusive to Ultimate Annual and Lifetime subscribers.
          </p>
          <a href="/pricing" className="inline-block px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold rounded-lg transition">
            View Ultimate Plans
          </a>
        </div>
      </div>
    );
  }

  const generateResponse = async () => {
    setGenerating(true);
    setResponse('');
    
    // Simulate API call with realistic delay
    const startTime = Date.now();
    
    setTimeout(() => {
      const responseTime = Date.now() - startTime;
      const tokenCount = 30 + Math.floor(Math.random() * 70);
      
      // Generate response based on temperature
      let responseText = '';
      if (temperature < 0.3) {
        responseText = 'Hello! I am an AI assistant. I can help answer questions, provide information, and assist with various tasks. How may I help you today?';
      } else if (temperature < 0.7) {
        responseText = "Greetings! I'm your AI assistant, ready to help with a wide range of tasks. Whether you need information, creative ideas, or just want to chat, I'm here for you. What would you like to explore today?";
      } else {
        responseText = "Hey there! 🌟 I'm your digital companion, bursting with ideas and ready to embark on an intellectual adventure with you! From solving complex problems to crafting imaginative stories, I'm your go-to AI. What exciting journey shall we begin today?";
      }
      
      setResponse(responseText);
      setMetadata({
        responseTime: `${responseTime}ms`,
        tokenCount,
        model,
        params: `T:${temperature.toFixed(1)}, P:${topP.toFixed(2)}`
      });
      setGenerating(false);
    }, 1000 + Math.random() * 1500);
  };

  const tabs = [
    { id: 'basics', label: 'Getting Started', icon: Play },
    { id: 'parameters', label: 'Parameter Exploration', icon: Settings },
    { id: 'visualization', label: 'Data Visualization', icon: BarChart3 },
    { id: 'advanced', label: 'Advanced Usage', icon: Code }
  ];

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="mb-10 text-center">
          <h1 className="text-5xl font-bold mb-4 text-white">Z.AI Interactive SDK Simulation</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Explore how different parameters affect AI responses in real-time.
            <span className="block mt-2 font-semibold text-yellow-400">
              How do temperature and top_p settings transform the same prompt into different answers?
            </span>
          </p>
          <div className="mt-4 inline-block px-4 py-2 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
            <p className="text-yellow-400 font-semibold">
              👑 Ultimate Tier Feature - Exclusive Access
            </p>
          </div>
        </header>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap border-b border-gray-700 mb-8">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 font-medium focus:outline-none transition ${
                  activeTab === tab.id
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                <Icon className="inline w-5 h-5 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* BASICS TAB */}
        {activeTab === 'basics' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-400" />
                SDK Installation
              </h2>
              <div className="mb-4">
                <p className="text-gray-300 mb-3">Install the ZAI SDK using pip:</p>
                <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
{`# Install latest version
pip install zai-sdk

# Or specify version
pip install zai-sdk==0.0.1`}
                </pre>
              </div>
            </div>

            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                <Code className="w-6 h-6 text-blue-400" />
                Basic Usage
              </h2>
              <pre className="bg-gray-900 text-blue-400 p-4 rounded-lg overflow-x-auto text-sm">
{`from zai import ZaiClient

client = ZaiClient(api_key="YOUR_API_KEY")

response = client.chat.completions.create(
    model="glm-4.5",
    messages=[
        {"role": "system", "content": "You are helpful."},
        {"role": "user", "content": "Hello!"}
    ],
    temperature=0.7
)

print(response.choices[0].message.content)`}
              </pre>
            </div>
          </div>
        )}

        {/* PARAMETERS TAB */}
        {activeTab === 'parameters' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Controls */}
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <h2 className="text-2xl font-bold mb-6 text-white">Parameter Controls</h2>
              
              {/* Model */}
              <div className="mb-6">
                <label className="block text-gray-300 font-medium mb-2">Model Selection</label>
                <select
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="w-full p-3 bg-gray-900 border border-gray-700 rounded-lg text-white"
                >
                  <option value="glm-4.5">GLM-4.5</option>
                  <option value="glm-4">GLM-4</option>
                  <option value="glm-3-turbo">GLM-3 Turbo</option>
                </select>
              </div>

              {/* Temperature */}
              <div className="mb-6">
                <div className="flex justify-between mb-2">
                  <label className="text-gray-300 font-medium">Temperature</label>
                  <span className="text-blue-400 font-bold">{temperature.toFixed(1)}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={temperature}
                  onChange={(e) => setTemperature(parseFloat(e.target.value))}
                  className="w-full"
                />
                <p className="text-sm text-gray-500 mt-2">
                  Lower = deterministic, Higher = creative
                </p>
              </div>

              {/* Top P */}
              <div className="mb-6">
                <div className="flex justify-between mb-2">
                  <label className="text-gray-300 font-medium">Top_p</label>
                  <span className="text-blue-400 font-bold">{topP.toFixed(2)}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={topP}
                  onChange={(e) => setTopP(parseFloat(e.target.value))}
                  className="w-full"
                />
                <p className="text-sm text-gray-500 mt-2">
                  Token selection diversity control
                </p>
              </div>

              {/* Messages */}
              <div className="mb-4">
                <label className="block text-gray-300 font-medium mb-2">System Message</label>
                <textarea
                  value={systemMessage}
                  onChange={(e) => setSystemMessage(e.target.value)}
                  className="w-full p-3 bg-gray-900 border border-gray-700 rounded-lg text-white"
                  rows="2"
                />
              </div>

              <div className="mb-6">
                <label className="block text-gray-300 font-medium mb-2">User Message</label>
                <textarea
                  value={userMessage}
                  onChange={(e) => setUserMessage(e.target.value)}
                  className="w-full p-3 bg-gray-900 border border-gray-700 rounded-lg text-white"
                  rows="3"
                />
              </div>

              <button
                onClick={generateResponse}
                disabled={generating}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 rounded-lg transition disabled:opacity-50"
              >
                {generating ? 'Generating...' : '✨ Generate Response'}
              </button>
            </div>

            {/* Response */}
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <h2 className="text-2xl font-bold mb-4 text-white">Generated Response</h2>
              <div className="min-h-[300px] p-4 bg-gray-900 rounded-lg border border-gray-700">
                {generating ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  </div>
                ) : response ? (
                  <p className="text-gray-200">{response}</p>
                ) : (
                  <p className="text-gray-500 italic">Adjust parameters and click "Generate Response"...</p>
                )}
              </div>

              {/* Metadata */}
              {metadata.responseTime && (
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div className="bg-gray-900 p-3 rounded-lg">
                    <p className="text-sm text-gray-500">Response Time</p>
                    <p className="font-medium text-white">{metadata.responseTime}</p>
                  </div>
                  <div className="bg-gray-900 p-3 rounded-lg">
                    <p className="text-sm text-gray-500">Token Count</p>
                    <p className="font-medium text-white">{metadata.tokenCount}</p>
                  </div>
                  <div className="bg-gray-900 p-3 rounded-lg">
                    <p className="text-sm text-gray-500">Model Used</p>
                    <p className="font-medium text-white">{metadata.model}</p>
                  </div>
                  <div className="bg-gray-900 p-3 rounded-lg">
                    <p className="text-sm text-gray-500">Parameters</p>
                    <p className="font-medium text-white">{metadata.params}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Other tabs can be added later */}
        {activeTab === 'visualization' && (
          <div className="text-center text-white py-20">
            <BarChart3 className="w-20 h-20 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400 text-xl">Data visualization coming soon...</p>
          </div>
        )}

        {activeTab === 'advanced' && (
          <div className="text-center text-white py-20">
            <Code className="w-20 h-20 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400 text-xl">Advanced features coming soon...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZaiSDKSimulation;
